import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useProcessing, {
    ProcessingEnvironment,
    ProcessingFlow,
} from '@/src/Processing/components/hooks/useProcessing';
import { useEventContext } from '@/src/v2/components/EventContext';
import EventPage from '../../chrome/EventPage';
import { ConfirmDialog, Crumb, Toggle } from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { useToast } from '../../components/roster';
import { TCard, TEmpty, TSkeleton } from '../../components/table';
import '../../pages-event.css';
import './processing.css';

/*
 * Processing — the gallery's flows. Each row: enable toggle, environment,
 * step chain, edit and delete. Same requests as the v2 page.
 */

interface StepInfo {
    name: string;
    saveOutput: boolean;
}

function parseSteps(flowDefinition: string): StepInfo[] {
    try {
        const parsed = JSON.parse(flowDefinition);
        if (!Array.isArray(parsed)) return [];
        return parsed.map((s: any) => ({
            name: s?.title || s?.name || '?',
            saveOutput: !!s?.saveOutput,
        }));
    } catch {
        return [];
    }
}

const chainText = (steps: StepInfo[]) =>
    steps.length ? steps.map((s) => s.name).join(' → ') : 'an empty flow';

function ProcessingBody() {
    const navigate = useNavigate();
    const { activationId: id, galleryId, resolved } = useEventContext();
    const {
        getProcessingFlows,
        getProcessingEnvironments,
        updateProcessingFlow,
        deleteProcessingFlow,
    } = useProcessing();
    const toast = useToast();

    const [flows, setFlows] = useState<ProcessingFlow[]>([]);
    const [envs, setEnvs] = useState<Record<number, string>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [busy, setBusy] = useState<Set<number>>(new Set());
    const [confirmOn, setConfirmOn] = useState<ProcessingFlow | null>(null);
    const [confirmDel, setConfirmDel] = useState<ProcessingFlow | null>(null);

    useEffect(() => {
        if (!resolved) return;
        if (galleryId == null) {
            setIsLoading(false);
            return;
        }
        let mounted = true;
        setIsLoading(true);
        setLoadError(false);
        Promise.all([getProcessingFlows(galleryId), getProcessingEnvironments()])
            .then(([flowRows, envRows]) => {
                if (!mounted) return;
                setFlows(Array.isArray(flowRows) ? flowRows : []);
                setEnvs(
                    Object.fromEntries(
                        (envRows ?? []).map((e: ProcessingEnvironment) => [e.id, e.environment]),
                    ),
                );
            })
            .catch(() => mounted && setLoadError(true))
            .finally(() => mounted && setIsLoading(false));
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolved, galleryId, attempt]);

    const markBusy = (flowId: number, on: boolean) =>
        setBusy((s) => {
            const next = new Set(s);
            if (on) next.add(flowId);
            else next.delete(flowId);
            return next;
        });

    const setEnabled = async (flow: ProcessingFlow, next: boolean) => {
        if (busy.has(flow.id)) return;
        markBusy(flow.id, true);
        setFlows((rows) => rows.map((f) => (f.id === flow.id ? { ...f, enabled: next } : f)));
        const ok = await updateProcessingFlow(flow.id, flow.flowDefinition, flow.environmentId, next).catch(
            () => null,
        );
        if (!ok) {
            setFlows((rows) => rows.map((f) => (f.id === flow.id ? { ...f, enabled: flow.enabled } : f)));
            toast.show(`Couldn’t turn the flow ${next ? 'on' : 'off'} — try again.`);
        } else {
            toast.show(next ? 'Flow on — new uploads will run it.' : 'Flow off.');
        }
        markBusy(flow.id, false);
    };

    const remove = async (flow: ProcessingFlow) => {
        if (busy.has(flow.id)) return;
        markBusy(flow.id, true);
        const ok = await deleteProcessingFlow(flow.id)
            .then(() => true)
            .catch(() => false);
        if (ok) {
            setFlows((rows) => rows.filter((f) => f.id !== flow.id));
            toast.show('Flow deleted.');
        } else {
            toast.show('Couldn’t delete the flow — try again.');
        }
        markBusy(flow.id, false);
    };

    const editorPath = (flowId?: number) =>
        flowId == null ? `/activations/${id}/processing/new` : `/activations/${id}/processing/${flowId}/edit`;

    const ready = resolved && !isLoading;
    const noGallery = ready && galleryId == null;
    const onCount = flows.filter((f) => f.enabled).length;
    const sub = !ready
        ? 'Loading…'
        : noGallery
          ? 'Flows attach to the event gallery'
          : loadError
            ? 'Couldn’t load flows'
            : flows.length === 0
              ? 'No flows — captures publish unprocessed'
              : `${flows.length} flow${flows.length === 1 ? '' : 's'} · ${onCount} on · run on every new upload`;

    const hasSaved = flows.some((f) => parseSteps(f.flowDefinition).some((s) => s.saveOutput));

    return (
        <div className="ev-page">
            <Crumb label="Capture Experience" onClick={() => navigate(`/activations/${id}/capture`)} />
            <PageHeadActions
                title="Processing"
                sub={sub}
                actions={
                    ready && !noGallery && !loadError ? (
                        <button type="button" className="tb-new" onClick={() => navigate(editorPath())}>
                            New flow
                        </button>
                    ) : undefined
                }
            />

            <TCard>
                {!ready ? (
                    <TSkeleton rows={3} />
                ) : noGallery ? (
                    <TEmpty message="This event has no gallery yet — processing flows attach to the gallery." />
                ) : loadError ? (
                    <TEmpty
                        message="Couldn’t load processing flows."
                        actionLabel="Retry"
                        onAction={() => setAttempt((a) => a + 1)}
                    />
                ) : flows.length === 0 ? (
                    <TEmpty
                        message="No flows yet. Captures publish exactly as shot."
                        actionLabel="New flow"
                        onAction={() => navigate(editorPath())}
                    />
                ) : (
                    flows.map((f) => {
                        const steps = parseSteps(f.flowDefinition);
                        const isBusy = busy.has(f.id);
                        const label = chainText(steps);
                        return (
                            <div className={f.enabled ? 'pr-row' : 'pr-row off'} key={f.id}>
                                <Toggle
                                    label=""
                                    aria-label={`${f.enabled ? 'Turn off' : 'Turn on'} flow: ${label}`}
                                    checked={!!f.enabled}
                                    disabled={isBusy}
                                    onChange={(e) =>
                                        e.target.checked ? setConfirmOn(f) : setEnabled(f, false)
                                    }
                                />
                                <span className="pr-env">{envs[f.environmentId] ?? `env ${f.environmentId}`}</span>
                                <span className="pr-chain">
                                    <span className="pr-steps" title={label}>
                                        {steps.length === 0 ? (
                                            <span className="pr-meta">empty flow</span>
                                        ) : (
                                            steps.map((s, i) => (
                                                <React.Fragment key={i}>
                                                    {i > 0 && <span className="ar">→</span>}
                                                    <span className={s.saveOutput ? 'sv' : undefined}>{s.name}</span>
                                                </React.Fragment>
                                            ))
                                        )}
                                    </span>
                                    <span className="pr-meta">
                                        {steps.length} step{steps.length === 1 ? '' : 's'}
                                        {f.enabled ? '' : ' · off'}
                                    </span>
                                </span>
                                <span className="pr-acts">
                                    <button
                                        type="button"
                                        className="tb-btn"
                                        disabled={isBusy}
                                        onClick={() => navigate(editorPath(f.id))}
                                    >
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        className="ev-del"
                                        aria-label={`Delete flow: ${label}`}
                                        title="Delete flow"
                                        disabled={isBusy}
                                        onClick={() => setConfirmDel(f)}
                                    >
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.7"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <path d="M4.5 6h11M8 6V4.5h4V6M6 6l.7 9.5h6.6L14 6M8.5 9v4M11.5 9v4" />
                                        </svg>
                                    </button>
                                </span>
                            </div>
                        );
                    })
                )}
            </TCard>

            {ready && hasSaved && (
                <div className="pr-legend">
                    <span className="dot" aria-hidden="true" />
                    Highlighted step = its output is saved to the gallery
                </div>
            )}

            <ConfirmDialog
                open={confirmOn != null}
                danger={false}
                title="Turn on this flow?"
                body={`Every photo uploaded to this event from now on will run through ${
                    confirmOn ? chainText(parseSteps(confirmOn.flowDefinition)) : 'this flow'
                }. Photos already in the gallery aren’t changed.`}
                actionLabel="Turn on"
                onConfirm={() => confirmOn && setEnabled(confirmOn, true)}
                onClose={() => setConfirmOn(null)}
            />
            <ConfirmDialog
                open={confirmDel != null}
                title="Delete this flow?"
                body={`${
                    confirmDel ? chainText(parseSteps(confirmDel.flowDefinition)) : 'This flow'
                } will be deleted and new captures will no longer run it. This can’t be undone.`}
                actionLabel="Delete flow"
                onConfirm={() => confirmDel && remove(confirmDel)}
                onClose={() => setConfirmDel(null)}
            />
            {toast.node}
        </div>
    );
}

export default function ProcessingV3() {
    return (
        <EventPage section="capture">
            <ProcessingBody />
        </EventPage>
    );
}
